import { Button } from "#components/ui/button"
import { useChatAgent } from "#hooks/use-chat-agent"
import { useI18n } from "#hooks/use-i18n"
import { AnimatePresence, motion } from "framer-motion"
import { AlertCircle, Trash2, X } from "lucide-react"
import { useEffect, useRef } from "react"
import { ChatInput } from "./chat-input"
import { ChatMessage } from "./chat-message"

interface ChatAgentProps {
  onClose?: () => void
}

export function ChatAgent({ onClose }: ChatAgentProps) {
  const { t } = useI18n()
  const { messages, isLoading, error, sendMessage, clearMessages } = useChatAgent()
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex flex-row items-center justify-between px-4 py-3 border-b shrink-0">
        <h2 className="text-lg font-semibold">{t("components.chat.title")}</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={clearMessages}
            disabled={messages.length === 0}
            title={t("components.chat.clearHistory")}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              title={t("components.chat.close")}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <p>{t("components.chat.startConversation")}</p>
            </div>
          ) : (
            messages
              .filter((message) => {
                if (message.role === "system") {
                  return false
                }
                if (
                  message.role === "assistant" &&
                  !message.content?.trim() &&
                  !message.toolCalls?.length
                ) {
                  return false
                }
                return true
              })
              .map((message) => <ChatMessage key={message.id} message={message} />)
          )}
          <div ref={messagesEndRef} />
        </div>
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: 20, height: 0 }}
              transition={{
                duration: 0.3,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="shrink-0 border-t bg-destructive/10 border-destructive/20 overflow-hidden"
            >
              <div className="flex items-start gap-3 px-4 py-3">
                <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-sm text-destructive break-words flex-1 min-w-0">{error}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="shrink-0 border-t">
          <ChatInput onSend={(message) => sendMessage(message, true)} isLoading={isLoading} />
        </div>
      </div>
    </div>
  )
}
